import { forwardRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  options: SelectOption[];
  placeholder?: string;
  error?: string;
  hint?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, options, placeholder, error, hint, id, className, ...props }, ref) => {
    const selectId    = id ?? `select-${Math.random().toString(36).slice(2, 8)}`;
    const errorId     = error ? `${selectId}-error` : undefined;
    const hintId      = hint  ? `${selectId}-hint`  : undefined;
    const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label htmlFor={selectId} className="text-sm font-medium text-[var(--color-text-primary)]">
            {label}
            {props.required && <span className="text-[var(--color-error)] ml-1" aria-hidden="true">*</span>}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            aria-invalid={!!error}
            aria-describedby={describedBy}
            className={cn(
              'w-full appearance-none px-3 py-2.5 pr-9 text-sm rounded-[var(--radius-md)]',
              'bg-[var(--color-bg-tertiary)] border text-[var(--color-text-primary)]',
              'transition-colors cursor-pointer',
              error
                ? 'border-[var(--color-error)]'
                : 'border-[var(--color-border)] focus:border-[var(--color-accent-primary)]',
              className,
            )}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>{placeholder}</option>
            )}
            {options.map((opt) => (
              <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                {opt.label}
              </option>
            ))}
          </select>
          <ChevronDown
            className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-secondary)] pointer-events-none"
            aria-hidden="true"
          />
        </div>
        {hint && !error && (
          <p id={hintId} className="text-xs text-[var(--color-text-secondary)]">{hint}</p>
        )}
        {error && (
          <p id={errorId} role="alert" className="text-xs text-[var(--color-error)]">{error}</p>
        )}
      </div>
    );
  }
);
Select.displayName = 'Select';
